import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { colorUtils } from '../theme';
import { getSeverityChipSx, getStatusChipSx, getStatusColor, getPriorityColor } from '../theme/mappers/ticketMappers';
import { TicketsService, OverviewData } from '../services/ticketsService';

const STATUS_LABELS: Record<string, string> = {
  new: 'New', assigned: 'Assigned', in_progress: 'In Progress',
  pending: 'Pending', resolved: 'Resolved', closed: 'Closed',
  escalated: 'Escalated', false_positive: 'False Positive',
};

const STATUS_ORDER = ['new', 'assigned', 'in_progress', 'pending', 'escalated', 'resolved', 'closed', 'false_positive'];

// ── Mock constants for sections that need realistic SOC numbers ───────────────

const MOCK_STATUS_BREAKDOWN = [
  { status: 'new',           label: 'New',          count: 14 },
  { status: 'assigned',      label: 'Assigned',      count: 19 },
  { status: 'in_progress',   label: 'In Progress',   count: 27 },
  { status: 'pending',       label: 'Pending',       count: 9  },
  { status: 'escalated',     label: 'Escalated',     count: 5  },
  { status: 'resolved',      label: 'Resolved',      count: 54 },
  { status: 'closed',        label: 'Closed',        count: 143 },
  { status: 'false_positive', label: 'False Positive', count: 17 },
];

const MOCK_BY_PRIORITY: Record<string, number> = {
  critical:  8,
  high:     34,
  medium:   91,
  low:     155,
};

const MOCK_TOP_CLIENTS = [
  { name: 'Seedamyl S.p.A.',    ticket_count: 38 },
  { name: 'TechnoLogica Srl',   ticket_count: 24 },
  { name: 'Meccanica Vicenza',  ticket_count: 15 },
];

const MOCK_ALERT_COUNT_24H = 1247;

const MOCK_TREND_12H = [28, 41, 35, 62, 54, 78, 91, 67, 83, 72, 58, 44];

function relativeTime(isoString: string | null): string {
  if (!isoString) return '—';
  const diff = Date.now() - new Date(isoString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const OverviewPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    TicketsService.getOverview()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const statusBreakdown = MOCK_STATUS_BREAKDOWN;
  const totalStatusCount = statusBreakdown.reduce((sum, s) => sum + s.count, 0);
  const topCategories = data?.top_categories ?? [];
  const maxCategoryCount = Math.max(1, ...topCategories.map(c => c.count));
  const trendData = MOCK_TREND_12H;
  const trendMax = Math.max(1, ...trendData);


  const getIntegrationColor = (status: string) => {
    switch (status) {
      case 'connected': return theme.palette.success.main;
      case 'degraded': return theme.palette.warning.main;
      case 'disconnected': return theme.palette.error.main;
      default: return theme.palette.text.disabled;
    }
  };

  // ─── Shared card wrapper style ──────────────────────────────────────────────
  const cardSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1.5,
    p: 2.5,
    height: '100%',
  };

  // ─── Section label style (same as filter labels in Tickets/Alerts) ──────────
  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
      variant="caption"
      sx={{
        color: theme.palette.text.tertiary,
        fontWeight: 700,
        display: 'block',
        mb: 1.5,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 10, pb: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Overview
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Security operations at a glance
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small">
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                sx={{
                  height: 40,
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.background.tertiary,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  minWidth: 120,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: theme.palette.background.secondary },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.background.default,
                    boxShadow: `0 0 0 1px ${theme.palette.text.primary}`,
                  },
                }}
              >
                <MenuItem value="24h" sx={{ fontSize: '0.8rem' }}>Last 24 hours</MenuItem>
                <MenuItem value="7d" sx={{ fontSize: '0.8rem' }}>Last 7 days</MenuItem>
                <MenuItem value="30d" sx={{ fontSize: '0.8rem' }}>Last 30 days</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={load}
              sx={{
                bgcolor: theme.palette.background.tertiary,
                color: theme.palette.info.main,
                borderRadius: 1,
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: theme.palette.info.main,
                  color: theme.palette.background.tertiary,
                  transform: 'rotate(180deg)',
                },
                transition: 'all 0.4s ease',
              }}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Severity KPI Cards ─────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Critical', key: 'critical', color: theme.palette.severity.critical },
            { label: 'High',     key: 'high',     color: theme.palette.severity.high },
            { label: 'Medium',   key: 'medium',   color: theme.palette.severity.medium },
            { label: 'Low',      key: 'low',      color: theme.palette.severity.low },
          ].map((kpi) => {
            const count = MOCK_BY_PRIORITY[kpi.key] ?? 0;
            return (
            <Grid key={kpi.label} size={{ xs: 6, sm: 3 }}>
              <Box sx={{
                ...cardSx,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.background.elevated,
                  transform: 'translateY(-2px)',
                },
              }}>
                <Typography sx={{
                  color: theme.palette.text.tertiary,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>
                  {kpi.label}
                </Typography>
                <Typography sx={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: kpi.color,
                  lineHeight: 1.1,
                }}>
                  {count}
                </Typography>
                {/*<Chip*/}
                {/*  label="active alerts"*/}
                {/*  size="small"*/}
                {/*  sx={{*/}
                {/*    ...getSeverityChipSx(kpi.color),*/}
                {/*    backgroundColor: colorUtils.withOpacity(kpi.color, 0.15),*/}
                {/*    fontSize: '0.65rem',*/}
                {/*    height: 22,*/}
                {/*    '& .MuiChip-label': { px: 0.75 },*/}
                {/*  }}*/}
                {/*/>*/}
              </Box>
            </Grid>
            );
          })}
        </Grid>

        {/* ── Three-Column Layout ────────────────────────────────────────────── */}
        <Grid container spacing={2.5}>

          {/* ═══ LEFT COLUMN — Input / Health ═══════════════════════════════════ */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* Integration Status — commented out for now */}
              {/* <Box sx={cardSx}>
                <SectionLabel>Integration Status</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {mockIntegrations.map((integration) => (
                    <Box key={integration.name} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getIntegrationColor(integration.status),
                          flexShrink: 0,
                          boxShadow: integration.status === 'connected'
                            ? `0 0 6px ${colorUtils.withOpacity(getIntegrationColor(integration.status), 0.5)}`
                            : 'none',
                        }} />
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {integration.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        {integration.lastSync}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box> */}

              {/* SLA Breaches */}
              <Box sx={cardSx}>
                <SectionLabel>SLA Breaches</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: (data?.sla_breach_count ?? 0) > 0 ? theme.palette.error.main : theme.palette.success.main,
                    lineHeight: 1.1,
                  }}>
                    {data?.sla_breach_count ?? '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    tickets past due
                  </Typography>
                </Box>
              </Box>

              {/* Alert Volume */}
              <Box sx={cardSx}>
                <SectionLabel>Alert Volume (24h)</SectionLabel>
                <Typography sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.1,
                }}>
                  {MOCK_ALERT_COUNT_24H.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  alerts ingested
                </Typography>
              </Box>

              {/* Dedup Reduction — commented out for now */}
              {/* <Box sx={cardSx}>
                <SectionLabel>Deduplication</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    color: theme.palette.severity.low,
                    lineHeight: 1.1,
                  }}>
                    {dedupPercent}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>
                    noise reduced
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  {rawAlerts.toLocaleString()} raw &rarr; {dedupAlerts.toLocaleString()} deduplicated
                </Typography>
              </Box> */}

              {/* Ticket Status Breakdown */}
              <Box sx={cardSx}>
                <SectionLabel>Ticket Status</SectionLabel>
                {/* Stacked bar */}
                <Box sx={{
                  display: 'flex',
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: 12,
                  mb: 2,
                }}>
                  {statusBreakdown.map((s) => (
                    <Box key={s.status} sx={{
                      width: `${(s.count / totalStatusCount) * 100}%`,
                      backgroundColor: getStatusColor(s.status, theme),
                      transition: 'width 0.3s ease',
                    }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {statusBreakdown.map((s) => (
                    <Box key={s.status} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(s.status, theme),
                          flexShrink: 0,
                        }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {s.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}>
                        {s.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* ═══ CENTER COLUMN — Analysis / Queue ══════════════════════════════ */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* My Open Tickets */}
              <Box sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionLabel>My Open Tickets</SectionLabel>
                  <Chip
                    label={`${data?.my_tickets.length ?? '—'}`}
                    size="small"
                    sx={{
                      backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      fontWeight: 700, fontSize: '0.70rem', borderRadius: 1, height: 22,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
                {(data?.my_tickets.length ?? 0) === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, py: 1 }}>
                    No open tickets assigned to you
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(data?.my_tickets ?? []).map((ticket) => (
                      <Box key={ticket.id} onClick={() => navigate('/tickets', { state: { openTicketId: ticket.id } })} sx={{
                        p: 1.5, borderRadius: 1, cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateX(4px)' },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden',
                            textOverflow: 'ellipsis', flex: 1, minWidth: 0,
                          }}>
                            {ticket.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                            <Chip label={ticket.status.replace('_', ' ')} size="small"
                              sx={getStatusChipSx(getStatusColor(ticket.status, theme))} />
                            <Chip label={ticket.priority} size="small"
                              sx={getSeverityChipSx(getPriorityColor(ticket.priority, theme))} />
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                          Opened {relativeTime(ticket.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Tickets in Review */}
              <Box sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionLabel>Awaiting Review</SectionLabel>
                  <Chip
                    label={`${data?.tickets_in_review?.length ?? '—'}`}
                    size="small"
                    sx={{
                      backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.15),
                      color: theme.palette.warning.main,
                      fontWeight: 700, fontSize: '0.70rem', borderRadius: 1, height: 22,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
                {(data?.tickets_in_review?.length ?? 0) === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, py: 1 }}>
                    No tickets awaiting AI suggestion review
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(data?.tickets_in_review ?? []).map((ticket) => (
                      <Box
                        key={ticket.id}
                        onClick={() => navigate('/tickets', { state: { openTicketId: ticket.id, openAISuggestions: true } })}
                        sx={{
                          p: 1.5, borderRadius: 1, cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateX(4px)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden',
                            textOverflow: 'ellipsis', flex: 1, minWidth: 0,
                          }}>
                            {ticket.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                            {(ticket.pending_suggestions ?? 0) > 0 && (
                              <Chip
                                label={`${ticket.pending_suggestions} pending`}
                                size="small"
                                sx={{
                                  backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.15),
                                  color: theme.palette.warning.main,
                                  fontWeight: 700, fontSize: '0.65rem', borderRadius: 1, height: 20,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                            <Chip label={ticket.priority} size="small"
                              sx={getSeverityChipSx(getPriorityColor(ticket.priority, theme))} />
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                          Opened {relativeTime(ticket.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Unassigned High-Priority Tickets */}
              <Box sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionLabel>Unassigned High-Priority</SectionLabel>
                  <Chip
                    label={`${data?.unassigned.length ?? '—'}`}
                    size="small"
                    sx={{
                      backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.15),
                      color: theme.palette.error.main,
                      fontWeight: 700, fontSize: '0.70rem', borderRadius: 1, height: 22,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
                {(data?.unassigned.length ?? 0) === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, py: 1 }}>
                    No unassigned critical or high tickets
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(data?.unassigned ?? []).map((ticket) => (
                      <Box key={ticket.id} onClick={() => navigate('/tickets', { state: { openTicketId: ticket.id } })} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 1.5, p: 1.5, borderRadius: 1, cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateX(4px)' },
                      }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {ticket.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                            {relativeTime(ticket.created_at)}
                          </Typography>
                        </Box>
                        <Chip label={ticket.priority} size="small"
                          sx={{ ...getSeverityChipSx(getPriorityColor(ticket.priority, theme)), flexShrink: 0 }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

            </Box>
          </Grid>

          {/* ═══ RIGHT COLUMN — Intelligence / Context ═════════════════════════ */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>

              {/* MTTR Comparison */}
              {/*{(() => {
                const socmateMin = data?.avg_mttr_minutes ?? null;
                const manualMin = data?.benchmark.manual_avg_minutes ?? 240;
                const soarMin = data?.benchmark.soar_avg_minutes ?? 30;
                const maxMin = manualMin;
                const rows = [
                  { label: 'Manual (no automation)', sublabel: 'Industry benchmark', value: manualMin, color: theme.palette.error.main },
                  { label: 'SOAR',                   sublabel: 'Industry benchmark', value: soarMin,   color: theme.palette.warning.main },
                  { label: 'Socmate',                sublabel: socmateMin !== null ? 'Live data' : 'No resolved tickets yet', value: socmateMin, color: theme.palette.success.main },
                ];
                return (
                  <Box sx={cardSx}>
                    <SectionLabel>MTTR Comparison</SectionLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      {rows.map((row) => (
                        <Box key={row.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.75 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: theme.palette.text.primary }}>
                                {row.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.7rem' }}>
                                {row.sublabel}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: row.color, fontFamily: 'monospace', fontSize: '0.92rem', flexShrink: 0, ml: 1 }}>
                              {row.value !== null ? `${row.value} min` : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 6, borderRadius: 3, backgroundColor: theme.palette.background.tertiary, overflow: 'hidden' }}>
                            <Box sx={{
                              height: '100%',
                              width: row.value !== null ? `${(row.value / maxMin) * 100}%` : '2%',
                              borderRadius: 3,
                              backgroundColor: row.color,
                              transition: 'width 0.5s ease',
                            }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 2, fontSize: '0.68rem', fontStyle: 'italic' }}>
                      {data?.benchmark.source ?? 'SANS SOC Survey 2025, IBM Cost of a Data Breach 2025'}
                    </Typography>
                  </Box>
                );
              })()}*/}

              {/* Top Attack Categories */}
              <Box sx={cardSx}>
                <SectionLabel>Top Attack Categories</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                  {topCategories.map((cat) => (
                    <Box key={cat.category}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: theme.palette.text.primary }}>
                        {cat.category}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: theme.palette.background.tertiary, overflow: 'hidden' }}>
                          <Box sx={{
                            height: '100%',
                            width: `${(cat.count / maxCategoryCount) * 100}%`,
                            borderRadius: 3,
                            backgroundColor: theme.palette.primary.main,
                            transition: 'width 0.4s ease',
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{
                          color: theme.palette.text.secondary, fontWeight: 700,
                          fontFamily: 'monospace', fontSize: '0.75rem',
                          width: 24, textAlign: 'right', flexShrink: 0,
                        }}>
                          {cat.count}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Top Affected Clients */}
              <Box sx={cardSx}>
                <SectionLabel>Top Affected Clients</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {MOCK_TOP_CLIENTS.map((client, index) => (
                    <Box key={client.name} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{
                          color: theme.palette.text.tertiary,
                          fontWeight: 700,
                          width: 18,
                          textAlign: 'center',
                          flexShrink: 0,
                        }}>
                          {index + 1}
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {client.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${client.ticket_count} tickets`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.background.tertiary,
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          borderRadius: 1,
                          height: 22,
                          flexShrink: 0,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Alert Volume Trend */}
              <Box sx={{ ...cardSx, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <SectionLabel>Alert Volume Trend</SectionLabel>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '3px',
                  flexGrow: 1,
                  minHeight: 80,
                }}>
                  {trendData.map((value, index) => (
                    <Box key={index} sx={{
                      flex: 1,
                      height: `${(value / trendMax) * 100}%`,
                      backgroundColor: index === trendData.length - 1
                        ? theme.palette.primary.main
                        : colorUtils.withOpacity(theme.palette.primary.main, 0.25),
                      borderRadius: '2px 2px 0 0',
                      transition: 'height 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
                      minWidth: 4,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        transform: 'scaleY(1.08)',
                        transformOrigin: 'bottom',
                      },
                    }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    12h ago
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    now
                  </Typography>
                </Box>
              </Box>

              {/* Top Technologies Impacted — commented out for now */}
              {/* <Box sx={cardSx}>
                <SectionLabel>Top Technologies Impacted</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {mockTopTechnologies.map((tech) => (
                    <Box key={tech.name} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tech.name}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}>
                        {tech.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box> */}

            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OverviewPage;
